import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography,
  Box, Chip, Stack, Divider, CircularProgress, Button, Link, Grid, Alert,
  useMediaQuery, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { getNormativaBandejaById } from "../../http/actions/BandejaNormativaAction";

const formatFecha = (value) =>
  value ? new Date(value).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" }) : "—";

/**
 * Modal de detalle de una norma para el SUSCRIPTOR.
 * Recibe normaId (el NormaScraperId) y carga el detalle completo al abrir
 * usando el endpoint existente /Normativa/ObtenerBandeja/{id}.
 */
const DetalleNormativaSuscriptorModal = ({ open, onClose, normaId }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");

  const [norma, setNorma] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !normaId) return;

    setLoading(true);
    setError("");
    setNorma(null);

    getNormativaBandejaById(normaId, token)
      .then((data) => {
        if (data) setNorma(data);
        else setError("No se pudo cargar el detalle de la norma.");
      })
      .catch(() => setError("No se pudo cargar el detalle de la norma."))
      .finally(() => setLoading(false));
  }, [open, normaId, token]);

  // Helpers para leer campos con tolerancia a mayúsculas/nombres
  const val = (obj, ...keys) => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
  };

  const nombre = val(norma, "nombre", "Nombre", "titulo", "Titulo");
  const descripcion = val(norma, "descripcion", "Descripcion", "contenido", "Contenido");
  const regulador = val(norma, "regulador", "Regulador");
  const pais = val(norma, "pais", "Pais");
  const tipoDoc = val(norma, "tipoDocumento", "TipoDocumento");
  const fecha = val(norma, "fecha", "Fecha", "fechaPublicacion", "FechaPublicacion");
  const link = val(norma, "linkSitioWeb", "LinkSitioWeb", "url", "Url");
  const rubros = val(norma, "rubros", "Rubros") || [];
  const compromisos = val(norma, "compromisos", "Compromisos") || [];
  const obligaciones = val(norma, "obligaciones", "Obligaciones") || [];
  const documentos = val(norma, "documentos", "Documentos") || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      scroll="paper"
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.35 }}>
        {loading ? "Cargando norma…" : (nombre || "Detalle de la norma")}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
          aria-label="cerrar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : norma ? (
          <Box>
            {/* Metadatos principales */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <GavelOutlinedIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Regulador</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{regulador || "—"}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PublicOutlinedIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>País</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{pais || "—"}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DescriptionOutlinedIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Tipo de documento</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{tipoDoc || "—"}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CalendarTodayOutlinedIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>Publicación</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatFecha(fecha)}</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            {/* Rubros */}
            {rubros.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>Rubros</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {rubros.map((r) => (
                    <Chip key={r.id ?? r.Id} label={r.nombre ?? r.Nombre} size="small" variant="outlined" color="primary" />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Descripción */}
            {descripcion && (
              <Box sx={{ mb: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Descripción</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line", lineHeight: 1.6 }}>
                  {descripcion}
                </Typography>
              </Box>
            )}

            {/* Compromisos */}
            {compromisos.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Compromisos</Typography>
                <Stack spacing={1}>
                  {compromisos.map((c) => (
                    <Box key={c.id ?? c.Id} sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1.5 }}>
                      <Typography variant="body2">{c.descripcion ?? c.Descripcion}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Obligaciones */}
            {obligaciones.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Obligaciones</Typography>
                <Stack spacing={1}>
                  {obligaciones.map((o) => (
                    <Box key={o.id ?? o.Id} sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1.5 }}>
                      <Typography variant="body2">{o.descripcion ?? o.Descripcion}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Documentos */}
            {documentos.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Documentos</Typography>
                <Stack spacing={0.5}>
                  {documentos.map((d) => {
                    const url = d.urlDocumento ?? d.UrlDocumento;
                    if (!url) return null;
                    return (
                      <Link
                        key={d.id ?? d.Id}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.875rem" }}
                      >
                        <OpenInNewIcon fontSize="small" />
                        Ver documento
                      </Link>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!loading && !error && link && (
          <Button
            variant="outlined"
            component="a"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
          >
            Ver en sitio oficial
          </Button>
        )}
        <Button variant="contained" onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetalleNormativaSuscriptorModal;